import i18n from '@/libs/i18n'
import store from '@/store'
import useEntityCrud from '@/composable/useEntityCrud'
import useEntityHelper from '@/composable/useEntityHelper'
import useErrorManager from '@/composable/useErrorManager'
import { ref, computed } from '@vue/composition-api'

export default function useWeeksList(type = 'week') {
  const ENDPOINT = 'week'
  const MODULE_NAME = 'weeks'
  const { getAlt, createTagIdArray } = useEntityHelper()
  function convertResource(resource, selectedLang) {
    return {
      ...resource,
      title: resource.title[selectedLang],
      fetus: resource.fetus ? resource.fetus[selectedLang] : '',
      mother: resource.mother ? resource.mother[selectedLang] : '',
      activities: resource.activities ? resource.activities[selectedLang] : '',
      doNotWorry: resource.doNotWorry ? resource.doNotWorry[selectedLang] : '',
      musts: resource.musts ? resource.musts[selectedLang] : '',
      shouldNot: resource.shouldNot ? resource.shouldNot[selectedLang] : '',
      keywords: resource.keywords ? resource.keywords[selectedLang] : '',
      suggestions: resource.suggestions && resource.suggestions[selectedLang],
      description: resource.description ? resource.description[selectedLang] : '',
      lang: selectedLang,
      weekThumbnail: resource.weekThumbnailFileId && resource.weekThumbnailFileId[selectedLang] ? resource.weekThumbnailFileId[selectedLang] : resource.weekThumbnailFileId,
      weekCoverImage: resource.weekCoverImageFileId && resource.weekCoverImageFileId[selectedLang] ? resource.weekCoverImageFileId[selectedLang] : resource.weekCoverImageFileId,
      user: resource.userId,
      writer: resource.writerId,
      editor: resource.editorId,
      tags: resource.tagIds,
      relatedArticles: resource.relatedArticleIds && resource.relatedArticleIds.length
        ? resource.relatedArticleIds.map(item => ({
          _id: item._id,
          title: item.title ? item.title[selectedLang] : '',
          articleThumbnailFileId: item.articleThumbnailFileId,
        })) : [],
      relatedQuestions: resource.relatedQuestionIds && resource.relatedQuestionIds.length
        ? resource.relatedQuestionIds.map(item => ({
          _id: item._id,
          question: item.question ? item.question[selectedLang] : '',
        })) : [],
      relatedServices: resource.relatedServiceIds && resource.relatedServiceIds.length
        ? resource.relatedServiceIds.map(item => ({
          _id: item._id,
          title: item.title ? item.title[selectedLang] : '',
          serviceThumbnailFileId: item.serviceThumbnailFileId,
        })) : [],
      relatedCategories: resource.relatedCategoryIds && resource.relatedCategoryIds.length
        ? resource.relatedCategoryIds.map(item => ({
          _id: item._id,
          title: item.title ? item.title[selectedLang] : '',
          articleCategoryThumbnailFileId: item.articleCategoryThumbnailFileId,
        })) : [],
      weekThumbnailAlt: getAlt('weekThumbnailFileId', resource, selectedLang),
      weekCoverImageAlt: getAlt('weekCoverImageFileId', resource, selectedLang),
      weekLink: { name: `pages-${type}s-edit`, params: { id: resource._id, lang: selectedLang } },
    }
  }

  const errorManager = useErrorManager()
  const entity = useEntityCrud(MODULE_NAME, ENDPOINT, convertResource)
  const { itemLoading } = entity

  // Table Handlers
  const tableColumns = computed(() => [
    {
      key: 'title', label: i18n.t('general.title'), type: 'truncate-text', textKey: 'title', imageKey: 'weekThumbnail', linkKey: 'weekLink', sortable: true,
    },
    {
      key: 'week', label: i18n.t('general.week'), icon: 'ClipboardIcon', sortable: true,
    },
    {
      key: 'user', label: i18n.t('general.creator'), type: 'user', icon: 'UserCheckIcon',
    },
    {
      key: 'createdAt', label: i18n.t('general.createdAt'), type: 'dateTime', sortable: true,
    },
    {
      key: 'updatedAt', label: i18n.t('general.updatedAt'), type: 'dateTime', sortable: true,
    },
    { key: 'actions', label: i18n.t('general.actions'), sortable: false },
  ])

  const articleOptions = ref([])
  const questionOptions = ref([])
  const serviceOptions = ref([])
  const categoryOptions = ref([])
  const weekOptions = ref(Array.from({ length: 52 }, (_, i) => i + 1))

  const fetchArticles = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, type: 'article',
        },
        endpoint: 'articles/relatedArticles/',
      })
      .then(response => {
        articleOptions.value = response.data.data.map(item => ({ _id: item._id, title: item.title[lang], articleThumbnailFileId: item.articleThumbnailFileId }))
      })
      .catch(err => {
        useErrorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchCategories = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, limit: 40,
        },
        endpoint: 'articles-categories',
      })
      .then(response => {
        categoryOptions.value = response.data.data.map(item => ({ _id: item._id, title: item.title[lang] }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchServices = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, limit: 40,
        },
        endpoint: 'services',
      })
      .then(response => {
        serviceOptions.value = response.data.data.map(item => ({ _id: item._id, title: item.title[lang], serviceThumbnailFileId: item.serviceThumbnailFileId }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchQuestions = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, limit: 40,
        },
        endpoint: 'questions',
      })
      .then(response => {
        questionOptions.value = response.data.data.map(item => ({ _id: item._id, title: item.title[lang] }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  return {
    ...entity,
    articleOptions,
    categoryOptions,
    convertResource,
    createTagIdArray,
    errorManager,
    fetchArticles,
    fetchCategories,
    fetchQuestions,
    fetchServices,
    questionOptions,
    serviceOptions,
    tableColumns,
    weekOptions,
  }
}
